import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoOperacao } from '../../interfaces/IArquivoOperacao';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import posicaoFinanceiraService, { IPosicaoFinanceiraPendencia, IPosicaoFinanceiraPendenciaFilter } from '../../services/posicaoFinanceiraService';
import lancamentoNovaService from '../../services/lancamentoNovaService';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const PER_PAGE = 8;

function statusInfo(a: IArquivoOperacao): { label: string; color: string; bg: string } {
    if (a.arquivoErro) return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (a.arquivoProcessando) return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (a.arquivoProcessou) return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    if (a.arquivoIniciouProcesso) return { label: 'INICIADO', color: '#009ef7', bg: '#009ef722' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

// ─── Modal de detalhe de lançamento ──────────────────────────────────────────

function LancamentoDetalheModal({ id, onClose }: { id: number; onClose: () => void }) {
    const [lancamento, setLancamento] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        lancamentoNovaService.buscarPorId(id).then(setLancamento).finally(() => setLoading(false));
    }, [id]);

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: '8px', padding: '24px', minWidth: '340px', maxWidth: '480px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)', position: 'relative',
            }} onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6c757d' }}>×</button>
                <h6 style={{ fontWeight: 700, marginBottom: '16px', color: '#343a40' }}>Detalhes do Lançamento</h6>
                {loading ? (
                    <div className="d-flex justify-content-center py-3">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : !lancamento ? (
                    <p className="text-muted text-center">Lançamento não encontrado.</p>
                ) : (
                    <table className="table table-sm table-borderless mb-0">
                        <tbody>
                            {[
                                { label: 'Histórico', val: lancamento.historico },
                                { label: 'Valor', val: BRL.format(Math.abs(lancamento.valorProvisaoRecolher ?? 0)) },
                                { label: 'Tipo', val: lancamento.tipoLancamento },
                                { label: 'Data', val: lancamento.dataLancamento ? moment(lancamento.dataLancamento).format('DD/MM/YYYY') : '—' },
                                { label: 'Status', val: lancamento.status },
                                { label: 'Conta', val: lancamento.codigoContabil },
                            ].map((r, i) => (
                                <tr key={i}>
                                    <td style={{ color: '#6c757d', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', paddingRight: '12px' }}>{r.label}</td>
                                    <td style={{ fontSize: '13px', color: '#212529' }}>{r.val || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// ─── Modal de visualização de posições financeiras de um arquivo ──────────────

const filtroVazioVisualizacao: IPosicaoFinanceiraPendenciaFilter = {
    somentePendentes: false,
    tipo: '',
    razaoSocial: '',
    numeroDocumento: '',
    contaContabil: '',
    dataProvisaoInicio: '',
    dataProvisaoFim: '',
    incluirSobrescritos: false,
};

function CheckIcon({ found, ids, onClick }: { found: boolean; ids: number[]; onClick: (id: number) => void }) {
    if (found && ids.length === 1) {
        return (
            <button onClick={() => onClick(ids[0])} className="btn btn-sm btn-link p-0"
                title="Ver detalhes do lançamento"
                style={{ color: '#198754', fontWeight: 700, fontSize: '15px', lineHeight: 1 }}>
                ✔
            </button>
        );
    }
    if (found && ids.length > 1) {
        return (
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {ids.map(id => (
                    <button key={id} onClick={() => onClick(id)} className="btn btn-sm btn-link p-0"
                        title={`Ver detalhes do lançamento ${id}`}
                        style={{ color: '#198754', fontWeight: 700, fontSize: '13px', lineHeight: 1 }}>
                        ✔
                    </button>
                ))}
            </div>
        );
    }
    return <span style={{ color: '#dc3545', fontWeight: 700, fontSize: '15px' }}>✘</span>;
}

export function VisualizarPosicaoFinanceiraModal({
    esteiraId,
    arquivoOperacaoId,
    nomeArquivo,
    onClose,
}: {
    esteiraId: number;
    arquivoOperacaoId: number;
    nomeArquivo: string;
    onClose: () => void;
}) {
    const [items, setItems] = useState<IPosicaoFinanceiraPendencia[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [filtros, setFiltros] = useState<IPosicaoFinanceiraPendenciaFilter>(filtroVazioVisualizacao);
    const [modalLancamentoId, setModalLancamentoId] = useState<number | null>(null);
    const [iniciado, setIniciado] = useState(false);

    const color = '#0d6efd';

    const carregar = useCallback((pg: number, pp: number, f: IPosicaoFinanceiraPendenciaFilter) => {
        setLoading(true);
        posicaoFinanceiraService.listarPendencias(esteiraId, { ...f, page: pg, perPage: pp, arquivoOperacaoId })
            .then(resp => {
                setItems(resp.items);
                setTotalPages(resp.totalPages);
                setTotal(resp.total);
                setPage(pg);
            })
            .finally(() => setLoading(false));
    }, [esteiraId, arquivoOperacaoId]);

    useEffect(() => {
        if (iniciado) return;
        setIniciado(true);
        carregar(1, 20, filtroVazioVisualizacao);
    }, [iniciado, carregar]);

    const setF = (key: keyof IPosicaoFinanceiraPendenciaFilter, value: any) =>
        setFiltros(prev => ({ ...prev, [key]: value }));

    const onBuscar = () => carregar(1, perPage, filtros);
    const onLimpar = () => { setFiltros(filtroVazioVisualizacao); carregar(1, perPage, filtroVazioVisualizacao); };
    const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !loading) onBuscar(); };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            overflowY: 'auto', padding: '24px 16px',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '1100px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.22)', position: 'relative',
            }} onClick={e => e.stopPropagation()}>

                {modalLancamentoId && (
                    <LancamentoDetalheModal id={modalLancamentoId} onClose={() => setModalLancamentoId(null)} />
                )}

                {/* Header */}
                <div style={{
                    background: color, color: '#fff', borderRadius: '8px 8px 0 0',
                    padding: '14px 20px', fontWeight: 700, fontSize: '14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <span>POSIÇÃO FINANCEIRA — {nomeArquivo}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '2px 12px', fontSize: '12px', fontWeight: 600 }}>{total}</span>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                </div>

                {/* Filtros */}
                <div style={{ borderBottom: `1px solid ${color}`, padding: '8px 12px', background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '5px', alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Provisão — início</div>
                            <input className="form-control form-control-sm" type="date"
                                value={filtros.dataProvisaoInicio ?? ''} onChange={e => setF('dataProvisaoInicio', e.target.value)}
                                onKeyDown={onKeyDown} style={{ width: '140px' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Provisão — fim</div>
                            <input className="form-control form-control-sm" type="date"
                                value={filtros.dataProvisaoFim ?? ''} onChange={e => setF('dataProvisaoFim', e.target.value)}
                                onKeyDown={onKeyDown} style={{ width: '140px' }} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={filtros.somentePendentes ?? false}
                                onChange={e => setF('somentePendentes', e.target.checked)} />
                            Só pendências
                        </label>
                        <select className="form-select form-select-sm" style={{ width: '130px' }}
                            value={filtros.encontrouProvisao === undefined ? '' : String(filtros.encontrouProvisao)}
                            onChange={e => setF('encontrouProvisao', e.target.value === '' ? undefined : e.target.value === 'true')}>
                            <option value="">Provisão: todos</option>
                            <option value="true">Provisão: encontrada</option>
                            <option value="false">Provisão: não encontrada</option>
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={filtros.incluirSobrescritos ?? false}
                                onChange={e => setF('incluirSobrescritos', e.target.checked)} />
                            Mostrar sobrescritos
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <input className="form-control form-control-sm" type="text" placeholder="Razão Social..."
                            value={filtros.razaoSocial ?? ''} onChange={e => setF('razaoSocial', e.target.value)}
                            onKeyDown={onKeyDown} style={{ flex: 2, minWidth: '120px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Nº Documento"
                            value={filtros.numeroDocumento ?? ''} onChange={e => setF('numeroDocumento', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '120px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Tipo"
                            value={filtros.tipo ?? ''} onChange={e => setF('tipo', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '90px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Conta contábil"
                            value={filtros.contaContabil ?? ''} onChange={e => setF('contaContabil', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '120px' }} />
                        <button className="btn btn-sm btn-primary"
                            style={{ padding: '2px 12px', fontSize: '12px', minWidth: '72px' }}
                            onClick={onBuscar} disabled={loading}>
                            {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Buscar'}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={onLimpar} disabled={loading}
                            style={{ padding: '2px 10px', fontSize: '12px' }}>×</button>
                    </div>
                </div>

                {/* Tabela */}
                <div style={{ overflowX: 'auto', background: '#fff' }}>
                    {loading && items.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center py-4" style={{ gap: '12px' }}>
                            <div className="spinner-border" style={{ color }} role="status" />
                            <span className="text-muted">Carregando...</span>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-muted py-3" style={{ fontSize: '13px' }}>Nenhum registro encontrado.</p>
                    ) : (
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px', minWidth: '820px' }}>
                            <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px' }}>Razão Social</th>
                                    <th style={{ whiteSpace: 'nowrap', padding: '8px 10px' }}>Data Provisão</th>
                                    <th style={{ whiteSpace: 'nowrap', padding: '8px 10px', textAlign: 'right' }}>Valor Provisão</th>
                                    <th style={{ padding: '8px 10px' }}>Nº Documento</th>
                                    <th style={{ padding: '8px 10px' }}>Conta Contábil</th>
                                    <th style={{ textAlign: 'center', padding: '8px 10px' }}>Provisão</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} style={{ opacity: loading ? 0.6 : 1 }}>
                                        <td style={{ padding: '7px 10px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {item.razaoSocial || '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                                            {item.dataProvisao ? moment(item.dataProvisao).format('DD/MM/YYYY') : '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                            {item.valorProvisao != null ? BRL.format(item.valorProvisao) : '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: '#6c757d' }}>
                                            {item.numeroDocumento || '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: '#6c757d' }}>
                                            {item.contaContabil || '—'}
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '7px 10px' }}>
                                            <CheckIcon found={item.encontrouProvisao} ids={item.lancamentoIds}
                                                onClick={setModalLancamentoId} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Paginação */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #e9ecef', flexWrap: 'wrap', gap: '6px' }}>
                        <button onClick={() => carregar(page - 1, perPage, filtros)} disabled={page <= 1 || loading}
                            className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>‹ Ant.</button>
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>
                            {loading
                                ? <span className="spinner-border spinner-border-sm" role="status" />
                                : `Pág. ${page} / ${totalPages} · ${total} registro${total !== 1 ? 's' : ''}`}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>Por página:</span>
                            {[20, 50, 100].map(n => (
                                <button key={n} onClick={() => { setPerPage(n); carregar(1, n, filtros); }}
                                    disabled={loading}
                                    className={`btn btn-sm ${perPage === n ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                    style={{ padding: '1px 8px', fontSize: '11px' }}>{n}</button>
                            ))}
                        </div>
                        <button onClick={() => carregar(page + 1, perPage, filtros)} disabled={page >= totalPages || loading}
                            className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>Próx. ›</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface Props {
    esteiraId: number;
    cartaoId: number;
    onProcessou?: () => void;
}

function ArquivosCartao({ esteiraId, cartaoId, onProcessou }: Props) {
    const [dados, setDados] = useState<IArquivoOperacao[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [tipoSelecionado, setTipoSelecionado] = useState<'SALDO_INICIAL' | 'POSICAO_FINANCEIRA'>('SALDO_INICIAL');
    const [visualizarArquivo, setVisualizarArquivo] = useState<IArquivoOperacao | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const buscar = useCallback(() => {
        if (!cartaoId || !esteiraId) return;
        setLoading(true);
        ArquivoOperacaoService.listarPorCartao(esteiraId, cartaoId, page, PER_PAGE)
            .then(res => {
                setDados(res.items);
                setTotal(res.total);
                setTotalPages(res.totalPages);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [esteiraId, cartaoId, page]);

    useEffect(() => { buscar(); }, [buscar]);

    // Auto-refresh enquanto algum arquivo estiver processando
    useEffect(() => {
        const emAndamento = dados.some(a => a.arquivoProcessando || (a.arquivoIniciouProcesso && !a.arquivoProcessou && !a.arquivoErro));
        if (!emAndamento) return;
        const t = setInterval(buscar, 8000);
        return () => clearInterval(t);
    }, [dados, buscar]);

    const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            Swal.fire({ text: 'Por favor, anexe apenas arquivos Excel (.xlsx)', icon: 'warning', timer: 3000, showConfirmButton: false });
            return;
        }

        setUploading(true);
        try {
            const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(
                file.name,
                file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            );
            await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
            await ArquivoOperacaoService.registrar(esteiraId, tipoSelecionado, generatedFileName, file.name, cartaoId);

            const msg = tipoSelecionado === 'POSICAO_FINANCEIRA'
                ? 'Arquivo de posição financeira enviado para processamento!'
                : 'Arquivo de saldo inicial enviado para processamento!';
            Swal.fire({ text: msg, icon: 'success', timer: 3000, showConfirmButton: false });
            setPage(1);
            buscar();
            onProcessou?.();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao enviar o arquivo.', icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [esteiraId, cartaoId, tipoSelecionado, buscar, onProcessou]);

    return (
        <div>
            {visualizarArquivo && (
                <VisualizarPosicaoFinanceiraModal
                    esteiraId={esteiraId}
                    arquivoOperacaoId={visualizarArquivo.id}
                    nomeArquivo={visualizarArquivo.nomeOriginal}
                    onClose={() => setVisualizarArquivo(null)}
                />
            )}

            {/* Upload */}
            <div className='card card-body mb-3 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                <div className='fw-bold mb-2' style={{ fontSize: 12, color: '#3F4254' }}>
                    <i className='fas fa-upload me-1' style={{ color: '#009ef7' }} />
                    Enviar arquivo (.xlsx)
                </div>
                <div className='d-flex align-items-center gap-2 flex-wrap'>
                    <div className='d-flex align-items-center gap-2'>
                        <label className='fw-semibold mb-0 text-nowrap' style={{ fontSize: 12 }}>Tipo:</label>
                        <select
                            className='form-select form-select-sm'
                            style={{ minWidth: 170 }}
                            disabled={uploading}
                            value={tipoSelecionado}
                            onChange={e => setTipoSelecionado(e.target.value as 'SALDO_INICIAL' | 'POSICAO_FINANCEIRA')}
                        >
                            <option value='SALDO_INICIAL'>Saldo Inicial</option>
                            <option value='POSICAO_FINANCEIRA'>Posição Financeira</option>
                        </select>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <input
                            ref={fileRef}
                            type='file'
                            accept='.xlsx'
                            className='form-control form-control-sm'
                            style={{ maxWidth: 260 }}
                            disabled={uploading}
                            onChange={handleUpload}
                        />
                        {uploading && <span className='spinner-border spinner-border-sm text-primary' />}
                    </div>
                </div>
            </div>

            {/* Lista */}
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='fw-bold' style={{ fontSize: 12, color: '#3F4254' }}>
                    <i className='fas fa-file-alt me-1' style={{ color: '#7239EA' }} />
                    Arquivos ({total})
                </span>
                <button className='btn btn-sm btn-icon btn-light-primary' onClick={buscar} title='Atualizar'>
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} style={{ fontSize: 11 }} />
                </button>
            </div>

            {loading && dados.length === 0 ? (
                <div className='text-center py-3'>
                    <span className='spinner-border spinner-border-sm text-primary me-2' />
                    <span style={{ fontSize: 12 }}>Carregando...</span>
                </div>
            ) : dados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px', color: '#a1a5b7', fontSize: 12 }}>
                    Nenhum arquivo encontrado.
                </div>
            ) : (
                <>
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {dados.map(arquivo => {
                            const st = statusInfo(arquivo);
                            const podeFiltrarPosicao = !arquivo.arquivoErro && arquivo.arquivoProcessou && arquivo.tipo === 'POSICAO_FINANCEIRA';
                            return (
                                <div key={arquivo.id} style={{
                                    padding: '8px 10px',
                                    borderBottom: '1px solid #f1f1f2',
                                    backgroundColor: 'white',
                                    borderRadius: 4,
                                    marginBottom: 4
                                }}>
                                    <div className='d-flex justify-content-between align-items-start'>
                                        <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                                            <div style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: '#181C32'
                                            }} title={arquivo.nomeOriginal}>
                                                {arquivo.nomeOriginal}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#a1a5b7', marginTop: 2 }}>
                                                {moment(arquivo.dataCadastro).format('DD/MM/YYYY HH:mm')}
                                                {arquivo.dataFimProcesso && (
                                                    <span> · concluído {moment(arquivo.dataFimProcesso).format('HH:mm')}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                                            {podeFiltrarPosicao && (
                                                <button
                                                    className='btn btn-sm btn-light-primary'
                                                    style={{ fontSize: 10, padding: '2px 8px', fontWeight: 600 }}
                                                    onClick={() => setVisualizarArquivo(arquivo)}
                                                    title='Visualizar posições financeiras deste arquivo'
                                                >
                                                    Visualizar
                                                </button>
                                            )}
                                            <span style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                color: st.color,
                                                backgroundColor: st.bg,
                                                padding: '2px 7px',
                                                borderRadius: 4,
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {st.label}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Paginação compacta */}
                    {totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-2'>
                            <span style={{ fontSize: 11, color: '#a1a5b7' }}>Pág. {page}/{totalPages}</span>
                            <div className='d-flex gap-1'>
                                <button className='btn btn-xs btn-light' style={{ padding: '2px 7px', fontSize: 11 }}
                                    disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                                <button className='btn btn-xs btn-light' style={{ padding: '2px 7px', fontSize: 11 }}
                                    disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ArquivosCartao;
